import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Star, MapPin, CheckCircle2, MessageCircle, Calendar, Heart,
  ChevronLeft, Shield, Award, Home, Search, User, ArrowLeft,
  Phone, Clock, Loader2, Languages as LanguagesIcon
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Separator } from "../components/ui/separator";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Calendar as CalendarComponent } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { format } from "date-fns";
import { toast } from "sonner";
import { API } from "../utils/api";

const LOGO_ICON = "https://customer-assets.emergentagent.com/job_emergent-website-3/artifacts/6i1oj5oc_BTR_Health_favicon__2_-removebg-preview.png";

function BottomNav() {
  const location = window.location.pathname;
  const navItems = [
    { icon: ArrowLeft, label: "Home", path: "/" },
    { icon: Search, label: "Search", path: "/search" }
  ];

  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 px-2 py-2 z-50 md:hidden">
      <div className="flex justify-around items-center">
        {navItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className={`flex flex-col items-center gap-1 px-3 py-1 rounded-lg transition-colors ${
              location === item.path ? "text-[#87CEEB]" : "text-slate-400 hover:text-slate-600"
            }`}
          >
            <item.icon className="w-5 h-5" />
            <span className="text-xs">{item.label}</span>
          </Link>
        ))}
      </div>
    </nav>
  );
}

export default function WorkerDetailPage() {
  const { workerId } = useParams();
  const navigate = useNavigate();
  const [worker, setWorker] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bookingDialog, setBookingDialog] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [bookingData, setBookingData] = useState({
    service_type: "",
    date: null,
    time: "",
    duration: 2,
    notes: "",
    full_name: "",
    email: "",
    phone: "",
    address: "",
    ndis_number: "",
    ndis_plan_type: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [pricingCategories, setPricingCategories] = useState([]);

  // Load NDIS pricing reference for the cap badge inside the booking dialog.
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/pricing/categories`);
        if (r.ok) {
          const d = await r.json();
          setPricingCategories(d.registration_groups || []);
        }
      } catch (_) { /* non-blocking */ }
    })();
  }, []);

  const planNorm = (raw) => {
    if (!raw) return null;
    const s = String(raw).toLowerCase();
    if (s.includes("self")) return "self";
    if (s.includes("ndia") || s.includes("agency")) return "ndia_managed";
    if (s.includes("plan")) return "plan_managed";
    if (s === "private") return "private";
    return null;
  };

  const capForWorker = () => {
    if (!worker) return null;
    const cat = worker.primary_service_category || "daily_personal_activities";
    const g = pricingCategories.find(c => c.key === cat);
    if (!g) return null;
    const standard = g.line_items.find(li => (li.name || "").includes("Standard") && (li.name || "").includes("Daytime"));
    return standard?.weekday ?? g.line_items[0]?.weekday ?? null;
  };

  useEffect(() => {
    fetchWorker();
    fetchReviews();
  }, [workerId]);

  const fetchWorker = async () => {
    try {
      const response = await fetch(`${API}/workers/${workerId}`);
      if (response.ok) {
        const data = await response.json();
        setWorker(data);
      } else {
        toast.error("Worker not found");
        navigate("/search");
      }
    } catch (error) {
      console.error("Failed to fetch worker:", error);
      toast.error("Failed to load worker details");
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      const response = await fetch(`${API}/reviews/worker/${workerId}`);
      if (response.ok) {
        const data = await response.json();
        setReviews(data);
      }
    } catch (error) {
      console.error("Failed to fetch reviews:", error);
    }
  };

  const handleProtectedAction = (action) => {
    // Public site — no gating. Anyone can request a booking.
    if (action === "book") {
      setBookingDialog(true);
    } else if (action === "message") {
      window.location.href = `mailto:hello@btrhealth.com.au?subject=Booking%20enquiry%20for%20${encodeURIComponent(worker.name)}`;
    }
  };

  const [showDisclaimer, setShowDisclaimer] = useState(false);

  const openDisclaimer = () => {
    if (!bookingData.service_type || !bookingData.date || !bookingData.time) {
      toast.error("Please fill in all required fields");
      return;
    }
    if (!bookingData.full_name || !bookingData.phone) {
      toast.error("Please enter your full name and phone number so BTR Health can get in touch");
      return;
    }
    setShowDisclaimer(true);
  };

  const handleBooking = async () => {
    setShowDisclaimer(false);
    setSubmitting(true);
    try {
      const response = await fetch(`${API}/public/bookings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          worker_id: workerId,
          service_type: bookingData.service_type,
          date: format(bookingData.date, "yyyy-MM-dd"),
          time: bookingData.time,
          duration: bookingData.duration,
          notes: bookingData.notes,
          participant_name: bookingData.full_name,
          participant_email: bookingData.email,
          participant_phone: bookingData.phone,
          participant_address: bookingData.address,
          ndis_number: bookingData.ndis_number,
          ndis_plan_type: bookingData.ndis_plan_type,
        }),
      });

      if (response.ok) {
        toast.success("Booking request sent! BTR Health will be in touch shortly.", { duration: 5000 });
        setBookingDialog(false);
        setBookingSuccess(true);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to create booking");
      }
    } catch (error) {
      toast.error("Failed to create booking");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-pulse text-center">
          <div className="w-24 h-24 bg-slate-200 rounded-full mx-auto mb-4" />
          <div className="h-6 bg-slate-200 rounded w-32 mx-auto" />
        </div>
      </div>
    );
  }

  if (!worker) return null;

  const timeSlots = [
    "09:00 AM", "10:00 AM", "11:00 AM", "12:00 PM",
    "01:00 PM", "02:00 PM", "03:00 PM", "04:00 PM", "05:00 PM"
  ];

  return (
    <div className="min-h-screen bg-slate-50 pb-20 md:pb-8">
      {/* Header */}
      <header className="bg-[#87CEEB] sticky top-0 z-40 shadow-sm">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => navigate(-1)}
              className="p-2 -ml-2 hover:bg-white/20 rounded-lg text-white"
              data-testid="back-btn"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <Link to="/" className="p-2 -ml-2 hover:bg-white/20 rounded-lg text-white flex items-center gap-2">
              <img src={LOGO_ICON} alt="BTR Health" className="h-7" />
              <span className="text-white font-bold">BTR Health</span>
            </Link>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 max-w-4xl">
        {/* Profile Header */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-6"
        >
          <div className="relative inline-block mb-4">
            <img
              src={worker.picture}
              alt={worker.name}
              className="w-32 h-32 rounded-full object-cover border-4 border-white shadow-lg"
              onError={(e) => {
                e.target.src = "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400";
              }}
            />
            {worker.verified && (
              <div className="absolute bottom-2 right-2 w-8 h-8 bg-[#87CEEB] rounded-full flex items-center justify-center border-2 border-white">
                <CheckCircle2 className="w-5 h-5 text-white" />
              </div>
            )}
          </div>
          
          <h1 className="text-2xl font-bold text-slate-900 mb-1" data-testid="worker-name">{worker.name}</h1>
          
          <div className="flex items-center justify-center gap-2 text-slate-600 mb-2">
            <MapPin className="w-4 h-4" />
            {worker.location}
          </div>
          
          <div className="flex items-center justify-center gap-2 mb-4">
            <Star className="w-5 h-5 text-amber-400 fill-amber-400" />
            <span className="font-semibold">{worker.rating}</span>
            <span className="text-slate-500">({worker.review_count} reviews)</span>
          </div>
          
          <p className="text-base text-slate-500 max-w-md mx-auto">
            Contact us or send a booking request to confirm availability.
          </p>
        </motion.div>

        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex gap-3 mb-6"
        >
          <Button
            variant="outline"
            className="flex-1 h-12 rounded-xl"
            onClick={() => handleProtectedAction("message")}
            data-testid="message-worker-btn"
          >
            <MessageCircle className="w-5 h-5 mr-2" />
            Message
          </Button>
          <Button
            className="flex-1 h-12 rounded-xl bg-[#87CEEB] hover:bg-[#6BB8DB]"
            onClick={() => handleProtectedAction("book")}
            data-testid="book-worker-btn"
          >
            <Calendar className="w-5 h-5 mr-2" />
            Book Now
          </Button>
        </motion.div>

        {/* Public site — no sign-in banner. Anyone can send a booking request. */}

        {/* Availability */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-2xl p-6 shadow-sm mb-4"
          data-testid="worker-availability-section"
        >
          <h2 className="font-semibold text-slate-900 mb-3">Availability</h2>
          {(worker.available_days?.length || worker.availability_blocks?.length) ? (
            <>
              {worker.available_days?.length > 0 && (
                <div className="mb-3">
                  <p className="text-xs uppercase tracking-wider text-slate-400 mb-2">Days</p>
                  <div className="flex flex-wrap gap-2">
                    {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((d) => {
                      const on = worker.available_days.includes(d);
                      return (
                        <span
                          key={d}
                          className={`w-10 h-10 rounded-xl text-xs font-semibold flex items-center justify-center ${
                            on ? "bg-emerald-100 text-emerald-700 border border-emerald-200" : "bg-slate-50 text-slate-300 border border-slate-100"
                          }`}
                        >
                          {d}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
              {worker.availability_blocks?.length > 0 && (
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-400 mb-2">Times</p>
                  <div className="flex flex-wrap gap-2">
                    {worker.availability_blocks.map((b) => (
                      <Badge key={b} className="bg-[#87CEEB]/10 text-[#5BA3C0] border-0 px-3 py-1">
                        {b}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <p className="text-sm text-slate-500">Contact to confirm availability.</p>
          )}
        </motion.div>

        {/* Skills */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="bg-white rounded-2xl p-6 shadow-sm mb-4"
        >
          <h2 className="font-semibold text-slate-900 mb-3">Skills & Services</h2>
          <div className="flex flex-wrap gap-2">
            {worker.skills.map((skill) => (
              <Badge key={skill} className="bg-[#87CEEB]/10 text-[#87CEEB] border-0 px-3 py-1">
                {skill}
              </Badge>
            ))}
          </div>
        </motion.div>

        {/* Languages */}
        {(() => {
          const rawLangs = worker.languages;
          const langList = Array.isArray(rawLangs)
            ? rawLangs
            : (typeof rawLangs === "string"
                ? rawLangs.split(",").map(s => s.trim()).filter(Boolean)
                : []);
          if (langList.length === 0) return null;
          return (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.28 }}
              className="bg-white rounded-2xl p-6 shadow-sm mb-4"
              data-testid="worker-languages-section"
            >
              <h2 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <LanguagesIcon className="w-4 h-4 text-[#5BA3C0]" />
                Languages spoken
              </h2>
              <div className="flex flex-wrap gap-2">
                {langList.map((lang) => (
                  <Badge key={lang} className="bg-[#87CEEB]/10 text-[#5BA3C0] border-0 px-3 py-1">
                    {lang}
                  </Badge>
                ))}
              </div>
            </motion.div>
          );
        })()}

        {/* About */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-2xl p-6 shadow-sm mb-4"
        >
          <h2 className="font-semibold text-slate-900 mb-3">About</h2>
          <p className="text-slate-600 leading-relaxed">{worker.bio}</p>
        </motion.div>

        {/* Verification */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-2xl p-6 shadow-sm mb-4"
        >
          <h2 className="font-semibold text-slate-900 mb-4">Verification</h2>
          <div className="grid grid-cols-2 gap-4">
            {[
              { icon: Shield, label: "Police Checked" },
              { icon: Award, label: "NDIS Registered" },
              { icon: CheckCircle2, label: "First Aid Certified" },
              { icon: Shield, label: "WWCC Verified" }
            ].map((item) => (
              <div key={item.label} className="flex items-center gap-3 p-3 bg-emerald-50 rounded-xl">
                <item.icon className="w-5 h-5 text-emerald-600" />
                <span className="text-sm text-emerald-700 font-medium">{item.label}</span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Reviews Section */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-2xl p-6 shadow-sm"
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-slate-900">Reviews</h2>
            <div className="flex items-center gap-1">
              <Star className="w-5 h-5 text-amber-400 fill-amber-400" />
              <span className="font-semibold">{worker.rating}</span>
              <span className="text-slate-500">({worker.review_count})</span>
            </div>
          </div>
          
          {reviews.length > 0 ? (
            <div className="space-y-4">
              {reviews.slice(0, 5).map((review) => (
                <div key={review.review_id} className="border-b border-slate-100 pb-4 last:border-0">
                  <div className="flex items-start gap-3">
                    <Avatar className="h-10 w-10">
                      <AvatarFallback className="bg-[#87CEEB]/10 text-[#87CEEB]">
                        {review.user_name?.charAt(0) || "U"}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-slate-900">{review.user_name}</span>
                        <div className="flex items-center gap-1">
                          {[...Array(5)].map((_, i) => (
                            <Star 
                              key={i} 
                              className={`w-3 h-3 ${i < review.rating ? "text-amber-400 fill-amber-400" : "text-slate-200"}`}
                            />
                          ))}
                        </div>
                      </div>
                      {review.comment && (
                        <p className="text-sm text-slate-600">{review.comment}</p>
                      )}
                      <p className="text-xs text-slate-400 mt-1">
                        {new Date(review.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6">
              <Star className="w-10 h-10 text-slate-200 mx-auto mb-2" />
              <p className="text-slate-500 text-sm">No reviews yet</p>
            </div>
          )}
        </motion.div>
      </main>

      {/* Booking Dialog */}
      <Dialog open={bookingDialog} onOpenChange={setBookingDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <img src={worker.picture} alt="" className="w-10 h-10 rounded-full object-cover" />
              Book {worker.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">

            <div className="space-y-2">
              <Label>Your Full Name *</Label>
              <Input
                placeholder="Chris Newman"
                value={bookingData.full_name}
                onChange={(e) => setBookingData({ ...bookingData, full_name: e.target.value })}
                data-testid="booking-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Your Email</Label>
              <Input
                type="email"
                placeholder="you@email.com"
                value={bookingData.email}
                onChange={(e) => setBookingData({ ...bookingData, email: e.target.value })}
                data-testid="booking-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Your Phone Number *</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type="tel"
                  placeholder="0400 000 000"
                  value={bookingData.phone}
                  onChange={(e) => setBookingData({ ...bookingData, phone: e.target.value })}
                  className="pl-10"
                  data-testid="booking-phone-input"
                />
              </div>
              <p className="text-xs text-slate-500">BTR Health will call or text you to confirm.</p>
            </div>

            <div className="space-y-2">
              <Label>Address for the shift</Label>
              <Input
                placeholder="12 Test St, Suburb VIC 3000"
                value={bookingData.address}
                onChange={(e) => setBookingData({ ...bookingData, address: e.target.value })}
                data-testid="booking-address-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>NDIS Number</Label>
                <Input
                  placeholder="43xxxxxxxx"
                  value={bookingData.ndis_number}
                  onChange={(e) => setBookingData({ ...bookingData, ndis_number: e.target.value })}
                  data-testid="booking-ndis-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Plan Type</Label>
                <Select
                  value={bookingData.ndis_plan_type}
                  onValueChange={(v) => setBookingData({ ...bookingData, ndis_plan_type: v })}
                >
                  <SelectTrigger data-testid="booking-plan-select">
                    <SelectValue placeholder="Select plan" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="self_managed">Self-managed</SelectItem>
                    <SelectItem value="plan_managed">Plan-managed</SelectItem>
                    <SelectItem value="ndia_managed">NDIA-managed</SelectItem>
                    <SelectItem value="private">Private (non-NDIS)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Service Type *</Label>
              <Select
                value={bookingData.service_type}
                onValueChange={(v) => setBookingData({ ...bookingData, service_type: v })}
              >
                <SelectTrigger data-testid="service-type-select">
                  <SelectValue placeholder="Select a service" />
                </SelectTrigger>
                <SelectContent>
                  {worker.skills.map((skill) => (
                    <SelectItem key={skill} value={skill}>{skill}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Date *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal"
                    data-testid="date-picker-btn"
                  >
                    <Calendar className="mr-2 h-4 w-4" />
                    {bookingData.date ? format(bookingData.date, "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <CalendarComponent
                    mode="single"
                    selected={bookingData.date}
                    onSelect={(d) => setBookingData({ ...bookingData, date: d })}
                    disabled={(date) => date < new Date()}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label>Time *</Label>
              <Select
                value={bookingData.time}
                onValueChange={(v) => setBookingData({ ...bookingData, time: v })}
              >
                <SelectTrigger data-testid="time-select">
                  <SelectValue placeholder="Select a time" />
                </SelectTrigger>
                <SelectContent>
                  {timeSlots.map((time) => (
                    <SelectItem key={time} value={time}>{time}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Duration (hours)</Label>
              <Select
                value={String(bookingData.duration)}
                onValueChange={(v) => setBookingData({ ...bookingData, duration: parseInt(v) })}
              >
                <SelectTrigger data-testid="duration-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4, 5, 6].map((h) => (
                    <SelectItem key={h} value={String(h)}>{h} hour{h > 1 ? "s" : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Textarea
                placeholder="Any special requirements or notes..."
                value={bookingData.notes}
                onChange={(e) => setBookingData({ ...bookingData, notes: e.target.value })}
                data-testid="booking-notes-input"
              />
            </div>

            <Separator />

            {/* Price breakdown removed — BTR Health subcontractor; we'll confirm pricing with you */}
            <div className="bg-slate-50 rounded-xl p-4 text-sm text-slate-600">
              <p className="font-medium text-slate-900 mb-1">No payment required now</p>
              <p>Pricing is handled by BTR Health on your behalf. You'll receive an invoice from us after the shift, in line with your NDIS plan.</p>
            </div>

            {/* Cancellation Policy */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
              <p className="text-xs text-amber-800">
                <strong>Cancellation Policy:</strong> Free cancellation up to 24 hours before your appointment. 
                Cancellations within 24 hours may be charged 50% of the booking fee.
              </p>
            </div>

            <Button
              className="w-full h-12 bg-[#87CEEB] hover:bg-[#6BB8DB] rounded-xl text-base font-semibold"
              onClick={openDisclaimer}
              disabled={submitting}
              data-testid="confirm-booking-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                "Request booking"
              )}
            </Button>
            
            <p className="text-xs text-center text-slate-500">
              No payment now — the worker will confirm first.
            </p>

            <button
              type="button"
              onClick={() => { setBookingDialog(false); navigate('/request-advisor'); }}
              className="w-full text-center text-sm text-[#87CEEB] hover:underline pt-2"
              data-testid="request-advisor-link"
            >
              Or have a BTR support advisor organise this for you →
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Booking Disclaimer */}
      <Dialog open={showDisclaimer} onOpenChange={setShowDisclaimer}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Before we send your request</DialogTitle>
            <DialogDescription className="pt-2 text-slate-700 leading-relaxed">
              We'll share your name, phone number and service notes with <strong>{worker.name}</strong> so they can review your booking.
              <br/><br/>
              <span className="block bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-900 text-sm">
                <strong>Heads up:</strong> we'll email you a short BTR Health <strong>service agreement</strong> to sign. Your worker can only confirm this booking once our team has approved your signed agreement — usually same business day.
              </span>
              <br/>
              For anything special — preferences, routines, access needs — please <strong>message the worker directly</strong> once they accept, or <a href="mailto:hello@btrhealth.com.au" className="text-[#87CEEB] underline">contact BTR Health</a> and we'll help.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 pt-4">
            <Button variant="outline" className="flex-1" onClick={() => setShowDisclaimer(false)}>Cancel</Button>
            <Button
              className="flex-1 bg-[#87CEEB] hover:bg-[#6BB8DB] text-white"
              onClick={handleBooking}
              disabled={submitting}
              data-testid="disclaimer-confirm-btn"
            >
              {submitting ? "Sending…" : "Send request"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Success dialog — public booking confirmation */}
      <Dialog open={bookingSuccess} onOpenChange={setBookingSuccess}>
        <DialogContent className="sm:max-w-md" data-testid="public-booking-success">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="text-2xl">✅</span> Request received
            </DialogTitle>
            <DialogDescription className="pt-2 text-slate-700 leading-relaxed">
              Thanks — BTR Health has your booking request for <strong>{worker.name}</strong>.
              <br /><br />
              Our team will review your details and confirm the shift with you shortly by email or phone. You don&apos;t need an account — we&apos;ll handle the rest for you.
              <br /><br />
              If it&apos;s urgent, reach us on <a href="mailto:hello@btrhealth.com.au" className="text-[#87CEEB] underline">hello@btrhealth.com.au</a>.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3 mt-4">
            <Button
              className="bg-[#87CEEB] hover:bg-[#6BB8DB] rounded-full"
              onClick={() => { setBookingSuccess(false); navigate("/search"); }}
              data-testid="booking-success-back"
            >
              Back to search
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <BottomNav />
    </div>
  );
}
